import { useEffect, useState, type FormEvent } from 'react'
import { LockKeyhole, LogIn, Mail, UserPlus } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { RegistrationConfig } from '../api/types'
import { useI18n } from '../i18n'
import { LanguageSelect } from './LanguageSelect'

export function RegisterForm() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [regConfig, setRegConfig] = useState<RegistrationConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<RegistrationConfig>('/auth/registration-config')
      .then(setRegConfig)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setPending(true); setError('')
    const form = new FormData(event.currentTarget)
    try {
      const result = await api.post<{ accessToken: string; user: { role: string } }>('/auth/register', {
        email: String(form.get('email')),
        username: String(form.get('username')),
        password: String(form.get('password')),
        invitationCode: String(form.get('invitationCode') ?? ''),
      })
      api.setSession(result.accessToken, result.user.role)
      navigate('/user/mail')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to register.') }
    finally { setPending(false) }
  }

  function handleSSOLogin() {
    window.location.href = `/api/v1/auth/sso/authorize`
  }

  if (loading) return <main className="grid min-h-screen place-items-center"><p className="text-muted-foreground">{t('common.loading')}</p></main>

  if (!regConfig?.publicRegistration) {
    return <main className="grid min-h-screen place-items-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4 sm:p-6">
      <section className="panel w-full max-w-[26rem] p-6 sm:p-8 shadow-lg text-center">
        <div className="mb-6 flex justify-center"><div className="rounded-xl bg-primary p-3 text-primary-foreground shadow-sm"><Mail size={24} /></div></div>
        <h1 className="text-xl font-bold tracking-tight">{t('auth.registerTitle')}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t('auth.registrationClosed')}</p>
        {regConfig?.oidcEnabled && (
          <div className="mt-6">
            <button onClick={handleSSOLogin} className="btn-secondary flex w-full items-center justify-center gap-2"><LogIn size={16} />{t('auth.ssoLogin')}</button>
          </div>
        )}
        <p className="mt-6 text-sm text-muted-foreground">{t('auth.hasAccount')} <Link to="/user/login" className="font-medium text-primary hover:underline">{t('common.signIn')}</Link></p>
      </section>
    </main>
  }

  return <main className="grid min-h-screen place-items-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4 sm:p-6">
    <section className="panel w-full max-w-[26rem] p-6 sm:p-8 shadow-lg">
      <div className="mb-7 flex items-center justify-between"><div className="flex items-center gap-3"><div className="rounded-xl bg-primary p-2.5 text-primary-foreground shadow-sm"><UserPlus size={20} /></div><span className="font-semibold tracking-tight">{t('common.appName')}</span></div><LanguageSelect /></div>
      <h1 className="text-2xl font-bold tracking-tight">{t('auth.registerTitle')}</h1><p className="mt-1.5 text-sm text-muted-foreground">{t('auth.registerSubtitle')}</p>
      <form className="mt-6 space-y-4" onSubmit={submit}>
        <label className="block text-sm font-medium">{t('common.email')}<input className="field mt-1.5" name="email" type="email" autoComplete="email" required /></label>
        <label className="block text-sm font-medium">{t('auth.username')}<input className="field mt-1.5" name="username" type="text" autoComplete="username" required /></label>
        <label className="block text-sm font-medium">{t('common.password')}<input className="field mt-1.5" name="password" type="password" autoComplete="new-password" minLength={8} required /></label>
        {regConfig.invitationRequired && (
          <label className="block text-sm font-medium">{t('auth.invitationCode')}<input className="field mt-1.5" name="invitationCode" type="text" required /></label>
        )}
        {error && <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive">{error}</p>}
        <button disabled={pending} className="btn-primary flex w-full items-center justify-center gap-2"><UserPlus size={16} />{pending ? t('common.loading') : t('auth.registerButton')}</button>
      </form>

      {regConfig.oidcEnabled && (
        <div className="mt-5">
          <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div><div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">{t('common.or')}</span></div></div>
          <button onClick={handleSSOLogin} className="btn-secondary flex w-full items-center justify-center gap-2"><LogIn size={16} />{t('auth.ssoLogin')}</button>
        </div>
      )}

      <p className="mt-5 text-center text-sm text-muted-foreground">{t('auth.hasAccount')} <Link to="/user/login" className="font-medium text-primary hover:underline">{t('common.signIn')}</Link></p>
    </section>
  </main>
}
